import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactSelect, {
  MultiValue,
  PropsValue,
  SingleValue,
  type StylesConfig,
} from "react-select";
import { useQuery } from "@tanstack/react-query";

import { getSelectData } from "src/api/reportApi";
import type { FilterValues } from "src/types/report";

type StaticSelectOption = {
  label: string;
  value: string | number;
};

type StaticSelectProps = {
  onChange: (value: string | number | Array<string | number>) => void;
  id?: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  api_endpoint?: string;
  static_options?: StaticSelectOption[];
  isMulti?: boolean;
  renderMenuInPortal?: boolean;
  // FilterValues, not plain scalars: a multi-select stores its selection as an
  // array under the same key (the dashboard's Products filter).
  formValues?: FilterValues;
  defaultValues?: FilterValues;
  onMenuOpen?: () => void;
  // When the resolved value is unset ("" or undefined), render this as a real
  // selected value (react-select's solid singleValue styling) instead of
  // falling back to `placeholder` (react-select's greyed-out, italic-ish
  // styling) — for a filter like Products where "unset" is itself a
  // meaningful choice ("All Products"), not merely "nothing picked yet".
  emptyLabel?: string;
  // Overrides how an option's label renders, in both the menu and the
  // current selection — the underlying `value` (what's actually submitted
  // as the filter) is untouched either way. Defaults to just capitalizing
  // the first letter. Used by the Products (drug_name) filter to mask real
  // drug names as "Product 1", "Product 2", … on screen.
  getOptionLabel?: (option: StaticSelectOption) => string;
  // Show the selected value's full text in a tooltip on hover. For the
  // Products filter, whose names ("Exparel 133mg/10ml vial") are always
  // clipped by the 230px filter panel. Opt-in rather than always on, so a
  // short value like Group By's "Month" doesn't pop a tooltip that only
  // repeats what is already on screen.
  showValueTooltip?: boolean;
};

export default function Select({
  onChange,
  id = "staticSelect",
  name = "staticSelect",
  className,
  disabled,
  placeholder,
  api_endpoint,
  static_options,
  formValues,
  defaultValues,
  isMulti = false,
  renderMenuInPortal = false,
  onMenuOpen,
  emptyLabel,
  getOptionLabel,
  showValueTooltip = false,
}: StaticSelectProps) {
  const { isLoading, data: options } = useQuery<StaticSelectOption[], Error>({
    queryKey: ["entity", id],
    queryFn: () => getSelectData(api_endpoint as string),
    enabled: !!api_endpoint,
  });
  const menuPortalTarget =
    renderMenuInPortal && typeof document !== "undefined"
      ? document.body
      : undefined;
  const selectStyles: StylesConfig<StaticSelectOption, boolean> =
    renderMenuInPortal
      ? {
          menuPortal: (base) => ({
            ...base,
            zIndex: 2000,
          }),
          menu: (base) => ({
            ...base,
            zIndex: 2000,
          }),
        }
      : {};

  const onChangeSelect = (
    option:
      | SingleValue<StaticSelectOption>
      | MultiValue<StaticSelectOption>,
  ) => {
    onChange(
      Array.isArray(option)
        ? option.map((entry) => entry.value)
        : ((option as unknown as { value: string | number })?.value ?? ""),
    )
  }

  // Whichever list this instance is actually fed from.
  const resolvedOptions =
    static_options && static_options.length > 0
      ? static_options
      : (options ?? []);

  const calculateSelectValue = () => {
    // Multi-select (the dashboard's Products filter): one option object per
    // selected value, so react-select renders a chip each. An empty selection
    // means "all" and shows the placeholder rather than emptyLabel's
    // pseudo-selection used below — a removable "All Products" chip would be a
    // chip whose × does nothing, and react-select would then report it back as
    // part of the selection when the next product is picked.
    if (isMulti) {
      const selected = formValues?.[id] ?? defaultValues?.[id];
      const values = Array.isArray(selected)
        ? selected
        : selected === undefined || selected === ""
          ? []
          : [selected];
      return values.map((value) => ({
        value,
        label:
          resolvedOptions.find((option) => option.value === value)?.label ??
          String(value),
      }));
    }

    if(formValues?.[id] || defaultValues?.[id]) {
      return {
        value:
          formValues?.[id] ??
          defaultValues?.[id],
        label:
          static_options && static_options.length > 0 ? 
            static_options?.find(
              (option) =>
                option.value ===
                (formValues?.[id] ??
                  defaultValues?.[id]),
            )?.label ?? "" :
            options?.find(
              (option) =>
                option.value ===
                (formValues?.[id] ??
                  defaultValues?.[id]),
            )?.label ?? ""
      } as unknown as PropsValue<StaticSelectOption>
    }
    if (emptyLabel) {
      return { value: "", label: emptyLabel } as unknown as PropsValue<StaticSelectOption>;
    }
    return null;
  }

  return (
    <ReactSelect<StaticSelectOption, boolean>
      inputId={id}
      name={name}
      className={className}
      value={calculateSelectValue()}
      options={api_endpoint ? options : (static_options ?? [])}
      onChange={onChangeSelect}
      isDisabled={disabled}
      // A multi-select has no "All Products" entry to pick (see
      // calculateSelectValue), so the clear × is how the user gets back to
      // "every product". Single selects keep their original behaviour: unset
      // is already shown as a real value via emptyLabel.
      isClearable={isMulti}
      placeholder={placeholder}
      formatOptionLabel={(option, { context }) => {
        const label = getOptionLabel
          ? getOptionLabel(option)
          : option.label.length > 0
            ? option.label.charAt(0).toUpperCase() + String(option.label).slice(1)
            : option.label;

        // Only the selected value is clipped — the menu lays its rows out at
        // full width — and a tooltip opening over the list while picking would
        // sit in the way. `context` is react-select's own signal for which of
        // the two it is rendering right now.
        if (!showValueTooltip || context !== "value") return label;

        // react-bootstrap's tooltip rather than a `title` attribute: the
        // native one is held back by a browser delay of about a second that
        // nothing in the page can shorten, and the point of this is to read a
        // clipped name at a glance. This is the same OverlayTrigger the widget
        // headers use (WidgetRenderer.tsx).
        //
        // The text is the DISPLAYED label, not option.label: a demo account
        // hovering a masked chip has to keep seeing "Product 1" rather than
        // the drug name the mask hides.
        return (
          <OverlayTrigger
            placement="auto"
            delay={{ show: 0, hide: 0 }}
            overlay={
              // The value is free text, so it is squeezed into an id rather
              // than used as one: aria-describedby is a space-separated token
              // list, and a product name has spaces in it.
              <Tooltip
                id={`${id}-${String(option.value).replace(/[^\w-]+/g, "-")}`}
                // Same reason as WidgetRenderer: Bootstrap's 1080 sits under
                // the sticky navbar's z-index (9999 in generic.scss).
                style={{ zIndex: 10000 }}
              >
                {label}
              </Tooltip>
            }
          >
            <span>{label}</span>
          </OverlayTrigger>
        );
      }}
      isLoading={isLoading}
      isMulti={isMulti}
      onMenuOpen={onMenuOpen}
      menuPortalTarget={menuPortalTarget}
      menuPosition={renderMenuInPortal ? "fixed" : "absolute"}
      menuShouldScrollIntoView={!renderMenuInPortal}
      styles={selectStyles}
    />
  );
}
